import { AlertTriangle, Home, RefreshCw, Send } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useRouteError } from 'react-router-dom';
import { Button } from '@/components/Button';
import {
  buildAppErrorReportPayload,
  reportAppError,
} from '@/services/appErrorReport.service';

type AppCrashFallbackProps = {
  error?: unknown;
  componentStack?: string;
  onReset?: () => void;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Something went wrong while loading this page.';
};

export default function AppCrashFallback({
  error,
  componentStack,
  onReset,
}: AppCrashFallbackProps) {
  const routeError = useRouteError();
  const [reportStatus, setReportStatus] = useState<'idle' | 'sending' | 'sent' | 'failed'>(
    'idle'
  );

  const resolvedError = error ?? routeError;
  const errorMessage = useMemo(() => getErrorMessage(resolvedError), [resolvedError]);

  const handleReport = async () => {
    try {
      setReportStatus('sending');
      await reportAppError(buildAppErrorReportPayload(resolvedError, componentStack));
      setReportStatus('sent');
    } catch {
      setReportStatus('failed');
    }
  };

  const handleReload = () => {
    if (onReset) {
      onReset();
      return;
    }
    window.location.reload();
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10 text-slate-900 dark:bg-slate-950 dark:text-white">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-xl flex-col items-center justify-center text-center">
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600 ring-1 ring-red-100 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
          <AlertTriangle className="h-7 w-7" />
        </div>

        <h1 className="text-2xl font-semibold tracking-normal md:text-3xl">
          Failed to load page
        </h1>
        <p className="mt-3 max-w-md text-sm leading-6 text-slate-600 dark:text-slate-300">
          We hit an unexpected issue while opening this page. You can try again or report it
          to the Quick Assist team.
        </p>

        <div className="mt-5 w-full rounded-md border border-slate-200 bg-white p-4 text-left dark:border-slate-800 dark:bg-slate-900">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Error
          </p>
          <p className="mt-2 break-words text-sm text-slate-700 dark:text-slate-200">
            {errorMessage}
          </p>
          {resolvedError instanceof Error && resolvedError.stack && (
            <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-words text-[11px] leading-5 text-slate-500 dark:text-slate-400">
              {resolvedError.stack}
            </pre>
          )}
        </div>

        <div className="mt-6 flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={handleReload}
            className="w-full gap-2 sm:w-auto"
          >
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
          <Button
            type="button"
            onClick={handleReport}
            loading={reportStatus === 'sending'}
            disabled={reportStatus === 'sent'}
            className="w-full gap-2 sm:w-auto"
          >
            <Send className="h-4 w-4" />
            {reportStatus === 'sent' ? 'Reported' : 'Report to Quick Assist'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              window.location.href = '/';
            }}
            className="w-full gap-2 sm:w-auto"
          >
            <Home className="h-4 w-4" />
            Home
          </Button>
        </div>

        {reportStatus === 'failed' && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-300">
            Could not send the report. Please try again.
          </p>
        )}
        {reportStatus === 'sent' && (
          <p className="mt-4 text-sm text-emerald-600 dark:text-emerald-300">
            Thanks. The Quick Assist team has been notified.
          </p>
        )}
      </div>
    </main>
  );
}
